import { ElementRef, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    BankAccount,
    GeneratePresignedUrlForPaymentMethodService,
    GeneratePresignedUrlRequest,
    UploadType,
    BankAccountType
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { ContentTypes, FeatureFlagService, StringI18nValidator } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    BehaviorSubject,
    combineLatest,
    merge,
    MonoTypeOperatorFunction,
    Observable,
    Subject
} from 'rxjs';
import { FileUpload, HttpMethod, Uploader } from 'rxjs-uploader';
import { debounceTime, first, map, shareReplay, startWith, takeUntil, tap } from 'rxjs/operators';
import {
    ACCOUNT_NUMBER_MAX_LENGTH,
    ACCOUNT_NUMBER_MIN_LENGTH,
    BANK_VERIFICATION_FILE_SIZE_LIMIT,
    ROUTING_NUMBER_LENGTH
} from '../../../constants/bank.constants';
import { BankAccountsService } from './bank-accounts.service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { ROUTING_NUMBER_INFO_URL } from '../constants';

@Injectable({ providedIn: 'root' })
export class AddBankAccountFormService {
    private _init$ = new Subject<void>();

    private _fileSizeLimitInMb = BANK_VERIFICATION_FILE_SIZE_LIMIT;

    private _accountNumbersDoNotMatchCopy = this._i18n({
        id: 'addBankAccountNumbersDoNotMatch',
        value: `Account numbers do not match.`
    });
    private readonly uploadWrongFileTypeErrorMessageI18N = this._i18n(
        'Your file upload must be in PDF, PNG, JPEG, or TIFF format.'
    );
    private readonly contentSizeErrorMessageI18N = this._i18n(
        'Your file exceeds the {{limit}}MB limit.',
        { limit: this._fileSizeLimitInMb }
    );
    private readonly genericErrorMessageI18N = this._i18n(
        'The upload was not successful, please try again and if the problem persists contact support at <a href="mailto:fasttrack-support@lsq.com" class="mail-to preserve-case">fasttrack-support@lsq.com</a>'
    );

    private _formGroupSubject = new BehaviorSubject<FormGroup>(null);
    public formGroup$ = this._formGroupSubject.asObservable();

    public fileUpload$: Observable<FileUpload>;
    public uploadFileSubject = new Subject();

    private _fileUploadSuccessfulSubject = new Subject<undefined>();
    public fileUploadSuccessful$ = this._fileUploadSuccessfulSubject.asObservable();

    private _fileUploadErrorSubject = new Subject<Error>();
    public fileUploadError$ = this._fileUploadErrorSubject.asObservable();

    private _hasFileSelectedSubject = new BehaviorSubject<boolean>(false);
    public hasFileSelected$ = this._hasFileSelectedSubject.asObservable();

    private _uploadErrorMessageSubject = new BehaviorSubject<string>(null);
    public uploadErrorMessage$ = this._uploadErrorMessageSubject.asObservable();

    private _isSubmittingSubject = new BehaviorSubject<boolean>(false);
    public isSubmitting$ = this._isSubmittingSubject.asObservable();
    public disableSubmit$: Observable<boolean>;

    public fileUploader: Uploader = new Uploader<FileUpload>({
        allowedContentTypes: ContentTypes.ImagesAndPdfs,
        fileSizeLimitMb: this._fileSizeLimitInMb,
        fileCountLimit: 1,
        requestUrl: '',
        uploadFileAsBody: true,
        disallowedContentTypeErrorMessage: () => this.uploadWrongFileTypeErrorMessageI18N,
        disallowedContentSizeErrorMessage: () => this.contentSizeErrorMessageI18N,
        allFilesQueuedCallback: async (fileUploads) => {
            // This waits for the form to be submitted.
            await this.uploadFileSubject.pipe(first()).toPromise();

            this.fileUploader.patchRequestOptions({
                method: HttpMethod.Put
            });
            const presignedUrl = await this._getPresignedUrl(fileUploads[0].file.type);
            this.fileUploader.setRequestUrl(presignedUrl);
            return fileUploads;
        },
        allFilesUploadedCallback: () => {
            this._fileUploadSuccessfulSubject.next();
        }
    });

    constructor(
        private _i18n: I18n,
        private _stringI18nValidator: StringI18nValidator,
        private _generatePresignedUrlService: GeneratePresignedUrlForPaymentMethodService,
        private _featureFlagService: FeatureFlagService,
        private _bankAccountsService: BankAccountsService
    ) {
        this.fileUploader.errorStream.subscribe((error) => {
            if (
                error.message === this.uploadWrongFileTypeErrorMessageI18N ||
                error.message === this.contentSizeErrorMessageI18N
            ) {
                this._uploadErrorMessageSubject.next(error.message);
            } else {
                this._uploadErrorMessageSubject.next(this.genericErrorMessageI18N);
            }
        });
    }

    public resetFileSelected(): void {
        this._hasFileSelectedSubject.next(false);
    }

    public initFormGroup(): FormGroup {
        this._init$.next();
        const formGroup = this.createBankingInfoFormGroup(
            takeUntil(this._init$),
            this._accountNumbersDoNotMatchCopy
        );
        this._formGroupSubject.next(formGroup);

        this.disableSubmit$ = combineLatest([
            formGroup.statusChanges,
            this.hasFileSelected$,
            this.uploadErrorMessage$
        ]).pipe(
            map(
                ([formValidity, hasFileSelcted, uploadErrorMessage]) =>
                    formValidity !== 'VALID' || !hasFileSelcted || !!uploadErrorMessage
            ),
            startWith(true)
        );

        return formGroup;
    }

    public async submitForm(
        isEnrolling = false,
        options?: { sellerKey: string; companyKey: string }
    ): Promise<void> {
        const formGroup = await this.formGroup$.pipe(first()).toPromise();
        formGroup.markAllAsTouched();

        if (formGroup.valid) {
            this._isSubmittingSubject.next(true);

            const bankAccount = new BankAccount({
                accountName: formGroup.get('accountName').value,
                accountNumber: formGroup.get('accountNumber').value,
                routingNumber: formGroup.get('routingNumber').value,
                bankName: formGroup.get('bankName').value,
                active: true
            });

            this.uploadFileSubject.next();
            const errorOrUndefined = await merge(this.fileUploadSuccessful$, this.fileUploadError$)
                .pipe(first())
                .toPromise();
            if (errorOrUndefined) {
                this._isSubmittingSubject.next(false);
                throw errorOrUndefined;
            }
            try {
                bankAccount.bankAccountKey =
                    await this._bankAccountsService.createBankAccountAndSubmitForVerification(
                        bankAccount,
                        isEnrolling,
                        options
                    );

                // call pmt-restapi endpoint to validate payment method with US Bank API
                if (this._featureFlagService.isOnSync(FeatureFlags.LegalEntityConnection)) {
                    bankAccount.isDefault = isEnrolling;
                    bankAccount.bankAccountType = BankAccountType.ACH;
                    await this._bankAccountsService.verifyPaymentMethod(bankAccount);
                }

                this._isSubmittingSubject.next(false);
            } catch (error) {
                this._isSubmittingSubject.next(false);
                throw error;
            }
        }
    }

    public createBankingInfoFormGroup(
        takeUntilFunction: MonoTypeOperatorFunction<any>,
        accountNumbersDoNotMatchErrorCopy: string
    ): FormGroup {
        const formGroup: FormGroup = new FormGroup({
            accountName: new FormControl('', [Validators.required]),
            accountNumber: new FormControl('', [
                Validators.required,
                this._stringI18nValidator.minLength(ACCOUNT_NUMBER_MIN_LENGTH),
                this._stringI18nValidator.maxLength(ACCOUNT_NUMBER_MAX_LENGTH)
            ]),
            verifyAccountNumber: new FormControl('', [Validators.required]),
            routingNumber: new FormControl('', [
                Validators.required,
                this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)
            ]),
            bankName: new FormControl('', [Validators.required])
        });

        const accountNumberControl = formGroup.get('accountNumber');
        accountNumberControl!.addValidators(
            this._stringI18nValidator.matchValueSource(
                () => formGroup?.get('verifyAccountNumber')!,
                accountNumbersDoNotMatchErrorCopy
            )
        );
        const verifyAccountNumberControl = formGroup.get('verifyAccountNumber');
        verifyAccountNumberControl!.addValidators(
            this._stringI18nValidator.matchValueConfirm(
                () => formGroup?.get('accountNumber')!,
                accountNumbersDoNotMatchErrorCopy
            )
        );

        // Try to get a bank name every time the `routingNumber` changes.
        formGroup
            .get('routingNumber')!
            .valueChanges.pipe(
                takeUntilFunction,
                tap(() => {
                    formGroup.get('routingNumber')!.updateValueAndValidity({ emitEvent: false });
                }),
                debounceTime(200)
            )
            .subscribe(async (routingNumber: string) => {
                if (routingNumber?.length === 9) {
                    await this.getBankNameAndHandleResponse(routingNumber, formGroup);
                    formGroup.get('routingNumber')!.updateValueAndValidity({ emitEvent: false });
                }
            });

        return formGroup;
    }

    public async getBankNameAndHandleResponse(
        routingNumber: string,
        formGroup: FormGroup
    ): Promise<void> {
        try {
            const response = await fetch(`${ROUTING_NUMBER_INFO_URL}${routingNumber}`);
            const responseBody = await response.json();
            const bankName = responseBody.customer_name;
            formGroup.patchValue({ bankName });
        } catch (error) {
            formGroup.get('bankName')!.enable();
        }
    }

    public redactAccountNumber(accountNumber: string): string {
        return '******' + accountNumber.substring(6);
    }

    public setFileSource(fileSourceElement: ElementRef): void {
        this.fileUpload$ = this.fileUploader
            .streamFileUploads(fileSourceElement.nativeElement as HTMLElement)
            .pipe(
                map(([fileUpload]) => fileUpload),
                shareReplay(1)
            );
    }

    public handleFileSelected(fileRef: string): void {
        this._hasFileSelectedSubject.next(!!fileRef);
        this._uploadErrorMessageSubject.next(null);
    }

    private async _getPresignedUrl(fileExtension: string): Promise<string> {
        fileExtension = fileExtension.slice(fileExtension.indexOf('/') + 1);
        try {
            const request = new GeneratePresignedUrlRequest({
                uploadType: UploadType.PAYMENT_VERIFICATION,
                fileExtension: fileExtension
            });
            return (await this._generatePresignedUrlService.executeOnce(request)).presignedUrl;
        } catch (error) {
            this._fileUploadErrorSubject.next(error);
            throw error;
        }
    }
}
